import React, { useRef, useState } from "react";
import {
    useFloating,
    autoUpdate,
    offset,
    flip,
    shift,
    useHover,
    useFocus,
    useDismiss,
    useRole,
    useInteractions,
    FloatingPortal,
    arrow,
} from "@floating-ui/react";
import { useIntl } from "react-intl";

type OwTooltipProps = {
    children: React.ReactNode;
    tooltipContent: React.ReactNode;
    // Applied to the wrapping <span>, which is the hover target.
    className?: string;
    placement?: "top" | "bottom" | "left" | "right";
    offsetDistance?: number;
    inModal?: boolean;
    translateContent?: boolean;
};

/**
 * Themed tooltip around any content, shown immediately on hover/focus. Same look as
 * OwIconTooltip, which is the icon-only variant; use this one when the hover target is
 * text or a group of elements.
 */
export const OwTooltip = ({
    children,
    tooltipContent,
    className,
    placement = "top",
    offsetDistance = 10,
    inModal = false,
    translateContent = false,
}: OwTooltipProps) => {
    const intl = useIntl();

    const [isTooltipOpen, setIsTooltipOpen] = useState(false);
    const arrowRef = useRef<HTMLDivElement>(null);

    const {
        x,
        y,
        strategy,
        refs,
        middlewareData,
        context,
        placement: finalPlacement,
    } = useFloating({
        open: isTooltipOpen,
        onOpenChange: setIsTooltipOpen,
        placement,
        middleware: [offset(offsetDistance), flip(), shift(), arrow({ element: arrowRef })],
        whileElementsMounted: autoUpdate,
    });

    const content =
        translateContent && typeof tooltipContent === "string"
            ? intl.formatMessage({ id: tooltipContent, defaultMessage: tooltipContent, description: "tooltip" })
            : tooltipContent;

    const hover = useHover(context, { move: false });
    const focus = useFocus(context);
    const dismiss = useDismiss(context);
    const role = useRole(context, { role: "tooltip" });

    const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);

    // flip() may move the tooltip to the opposite side; the arrow follows the final side.
    const arrowSide = finalPlacement.startsWith("top")
        ? "bottom"
        : finalPlacement.startsWith("bottom")
          ? "top"
          : finalPlacement.startsWith("left")
            ? "right"
            : "left";

    return (
        <>
            {/* tabIndex makes the span focusable so useFocus opens the tooltip for keyboard users. */}
            <span ref={refs.setReference} className={className} tabIndex={0} {...getReferenceProps()}>
                {children}
            </span>
            {isTooltipOpen && (
                <FloatingPortal>
                    <div
                        className="tooltip-container p-2 tooltip-bg tooltip-text rounded shadow-sm"
                        ref={refs.setFloating}
                        style={{
                            position: strategy,
                            top: y ?? 0,
                            left: x ?? 0,
                            width: "max-content",
                            maxWidth: "250px",
                            zIndex: inModal ? 999999999 : 1000,
                        }}
                        {...getFloatingProps()}>
                        <div className="text-wrap">{content}</div>
                        <div
                            ref={arrowRef}
                            className="tooltip-arrow"
                            style={{
                                position: "absolute",
                                width: "8px",
                                height: "8px",
                                background: "inherit",
                                visibility: middlewareData.arrow ? "visible" : "hidden",
                                transform: "rotate(45deg)",
                                top: middlewareData.arrow?.y != null ? middlewareData.arrow.y : "",
                                left: middlewareData.arrow?.x != null ? middlewareData.arrow.x : "",
                                [arrowSide]: "-4px",
                            }}
                        />
                    </div>
                </FloatingPortal>
            )}
        </>
    );
};

export default OwTooltip;
